import React, { useState } from 'react';
import { AxiosInstance } from './utils';

interface NewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewsletterModal: React.FC<NewsletterModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await AxiosInstance.post('/api/newslettersubscribe', { "email" : email });
      
      if (response.data.result === 'Success') {
        setMessage({ type: 'success', text: 'Thank you for subscribing to our newsletter!' });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to subscribe. Please try again.' });
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'An error occurred. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h2>Subscribe to Our Newsletter</h2>
        <p>Stay updated with the latest news and events from BSides Bangalore.</p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              disabled={isLoading}
            />
          </div>
          
          {message && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}
          
          <div className="form-actions">
            <button type="submit" disabled={isLoading || !email}>
              {isLoading ? 'Subscribing...' : 'Subscribe'}
            </button>
            <button type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
          </div>
        </form>
      </div>
      
      <style dangerouslySetInnerHTML={{ __html: `
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.3));
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999999;
          backdrop-filter: blur(4px);
        }
        
        .modal-content {
          background: linear-gradient(145deg, #ffffff, #f8f9fa);
          padding: 2.5rem;
          border-radius: 16px;
          max-width: 500px;
          width: 90%;
          position: relative;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.2);
          animation: modalSlideIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .modal-close {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          background: transparent;
          border: none;
          font-size: 1.75rem;
          cursor: pointer;
          color: #666;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .modal-close:hover {
          background: rgba(0, 0, 0, 0.1);
          color: #333;
          transform: scale(1.1);
        }
        
        h2 {
          margin-top: 0;
          color: #2c3e50;
          font-size: 1.8rem;
          font-weight: 700;
          letter-spacing: -0.5px;
          text-align: center;
          margin-bottom: 0.5rem;
        }
        
        p {
          color: #5a6c7d;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
          line-height: 1.6;
          text-align: center;
          font-weight: 500;
        }
        
        .form-group {
          margin-bottom: 1.5rem;
        }
        
        input[type="email"] {
          width: 100%;
          padding: 1rem;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          background: #fff;
          box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        input[type="email"]:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
          transform: translateY(-1px);
        }
        
        input[type="email"]::placeholder {
          color: #adb5bd;
          font-style: italic;
        }
        
        .message {
          margin: 1.5rem 0;
          padding: 1rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          text-align: center;
          animation: messageSlideIn 0.3s ease;
        }
        
        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .message.success {
          background: linear-gradient(135deg, #d4edda, #c3e6cb);
          color: #155724;
          border: 1px solid #b8dacc;
          box-shadow: 0 4px 6px rgba(25, 135, 84, 0.1);
        }
        
        .message.error {
          background: linear-gradient(135deg, #f8d7da, #f5c6cb);
          color: #721c24;
          border: 1px solid #f1b0b7;
          box-shadow: 0 4px 6px rgba(220, 53, 69, 0.1);
        }
        
        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-top: 1rem;
        }
        
        button {
          padding: 0.875rem 2rem;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          font-size: 1.1rem;
          font-weight: 600;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        button[type="submit"] {
          background: linear-gradient(135deg, #007bff, #0056b3);
          color: white;
          border: 2px solid transparent;
        }
        
        button[type="submit"]:hover:not(:disabled) {
          background: linear-gradient(135deg, #0056b3, #004085);
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0, 123, 255, 0.3);
        }
        
        button[type="submit"]:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
        }
        
        button[type="button"] {
          background: linear-gradient(135deg, #6c757d, #5a6268);
          color: white;
          border: 2px solid transparent;
        }
        
        button[type="button"]:hover:not(:disabled) {
          background: linear-gradient(135deg, #5a6268, #495057);
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(108, 117, 125, 0.3);
        }
        
        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }
        
        /* Subtle decorative elements */
        .modal-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #007bff, #28a745, #ffc107);
          border-radius: 16px 16px 0 0;
        }
        
        /* Responsive adjustments */
        @media (max-width: 480px) {
          .modal-content {
            padding: 1.25rem;
            width: 95%;
            max-width: 400px;
          }
          
          h2 {
            font-size: 1.3rem;
            margin-bottom: 0.25rem;
          }
          
          p {
            font-size: 0.95rem;
            margin-bottom: 1rem;
          }
          
          .form-group {
            margin-bottom: 1rem;
          }
          
          input[type="email"] {
            padding: 0.875rem;
            font-size: 1rem;
          }
          
          .form-actions {
            flex-direction: column;
            gap: 0.75rem;
          }
          
          button {
            width: 100%;
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
          }
        }
        
        @media (max-width: 360px) {
          .modal-content {
            padding: 1rem;
            width: 98%;
            max-width: 340px;
          }
          
          h2 {
            font-size: 1.2rem;
          }
          
          p {
            font-size: 0.9rem;
          }
          
          input[type="email"] {
            padding: 0.75rem;
            font-size: 0.95rem;
          }
          
          button {
            padding: 0.625rem 1.25rem;
            font-size: 0.95rem;
          }
        }
      `}} />
    </div>
  );
};

export default NewsletterModal;